<template>
    <div>
        <headline>
            Buchungsregeln
            <template v-slot:more>
                <user-groups class="mr-2"></user-groups>
                <a href="/admin/pages/zones">
                    <button-secondary>
                        <i class="fa fa-pencil mr-2"></i>
                        Zonen verwalten
                    </button-secondary>
                </a>
            </template>
        </headline>

        <loading v-show="isLoading"></loading>

        <alert-dialog v-show="invalidRules.length > 0">
            <p class="mb-4">Folgende Regeln sind ungültig und müssen entfernt werden:</p>
            <p v-for="rule in invalidRules" class="mb-2">
                <span class="text-red-500">Regel ist nicht immer anwendbar:</span>
                {{ rule.user_group.name }} - {{ rule.zone.name }}
                <button-delete @click="trash(rule.id)" class="ml-4">
                    <i class="fa fa-times mr-2"></i> Regel entfernen
                </button-delete>
            </p>
            Die fehlerhaften Regeln werden in der Matrix <span class="font-bold">nicht</span> dargestellt.
        </alert-dialog>

        <card-full-invisible v-show="!isLoading">
            <template v-slot:header>Matrix Modus</template>

                <div class="mb-8 grid grid-cols-2 gap-2 rounded-md bg-white shadow rounded-lg p-4">
                    <select-menu v-model.number="userGroup"
                                 :text="['Alle'].concat(userGroups.map(u => u.name))"
                                 :values="[null].concat(userGroups.map(u => u.id))">
                        	Usergruppe Filtern
                    </select-menu>

                    <select-menu v-model.number="zone"
                                 :text="['Alle'].concat(zones.map(u => u.name))"
                                 :values="[null].concat(zones.map(u => u.id))">
                        Zonen filtern
                    </select-menu>
                </div>

                <tailwind-table :header="header">
                    <tr v-for="(zone, i) in zonesFiltered" :class="{'bg-white': i%2}">
                        <td class="px-6 py-3 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                            {{ zone.name }}
                        </td>
                        <td v-for="(userGroup) in userGroupsFiltered" class="px-6 py-3">
                            <a @click="toggle(zone.id, userGroup.id)" class="cursor-pointer">
                                <i class="fa" :class="{
                                    'fa-check-circle text-green-500':has(zone.id, userGroup.id),
                                    'fa-circle-thin text-gray-400': !has(zone.id, userGroup.id),
                                }"></i>
                            </a>
                        </td>
                    </tr>
                </tailwind-table>
        </card-full-invisible>
    </div>
</template>

<script>
import UserGroups from "../components/UserGroups";
import ButtonPrimary from "../components/UI/ButtonPrimary";
import Modal from "../components/Modal";
import ConfirmDialog from "../components/UI/ConfirmDialog";
import Loading from "../components/UI/Loading";
import ButtonWhite from "../components/UI/ButtonWhite";
import Headline from "../components/UI/Headline";
import InputField from "../components/UI/InputField";
import CardFullInvisible from "../components/UI/CardFullInvisible";
import ButtonDelete from "../components/UI/ButtonDelete";
import ButtonSecondary from "../components/UI/ButtonSecondary";
import SelectMenu from "../components/UI/SelectMenu";
import TailwindTable from "../components/UI/TailwindTable.vue";
import AlertDialog from "../components/UI/AlertDialog.vue";

export default {
    name: "Allocations",
    components: {
        AlertDialog,
        TailwindTable,
        SelectMenu,
        ButtonSecondary,
        ButtonDelete,
        CardFullInvisible,
        InputField,
        Headline,
        ButtonWhite,
        Loading,
        ConfirmDialog,
        Modal,
        ButtonPrimary,
        UserGroups,
    },

    props: ['group'],

    data: () => ({
        zones: [],
        userGroups: [],
        allocations: [],

        userGroup: null, // filter a specific item
        zone: null, // filter a specific item

        loading: 0,
    }),

    created() {
        this.load();
    },

    computed: {
        invalidRules() {
            return this.allocations.filter(a => !a.always_usable);
        },
        validRules() {
            return this.allocations.filter(a => a.always_usable);
        },
        header() {
            return [''].concat(this.userGroupsFiltered.map(u => u.name));
        },
        userGroupsFiltered() {
            if (this.userGroup) {
                return this.userGroups.filter(g => g.id === this.userGroup);
            }

            return this.userGroups;
        },
        zonesFiltered() {
            if (this.zone) {
                return this.zones.filter(g => g.id === this.zone);
            }

            return this.zones;
        },
        isLoading() {
            return this.loading > 0;
        },
        dragOptions() {
            return {
                animation: 200,
                group: "description",
                disabled: false,
                ghostClass: "ghost"
            };
        }
    },

    methods: {
        load() {
            this.loading = 1;
            window.axios.get('/admin/allocations').then(result => {
                this.allocations = result.data.data;
                this.zones = result.data.zones;
                this.userGroups = result.data.user_groups;
                this.loading--;
            });
        },
        create(zone, userGroup) {
            this.loading = 1;
            window.axios.post('/admin/allocations', {
                user_group: userGroup,
                zone: zone,
                always_usable: true,
            }).then(() => {
                this.loading -= 1;
                this.$root.$emit('alert', 'Neue Regel erstellt.');
                this.load();
                this.showModal = false;
            }).catch(error => {
                this.loading -= 1;
                this.$root.$emit('error', error);
            })
        },
        trash(id) {
            this.loading = 1;
            window.axios.post(`/admin/allocations/${id}`, {
                _method: 'delete',
            }).then(() => {
                this.loading -= 1;
                this.$root.$emit('alert', 'Regel gelöscht.');
                this.load();
            }).catch(error => {
                this.loading -= 1;
                this.$root.$emit('error', error);
            })
        },
        has(zone, userGroup) {
            return this.validRules.filter(item => (item.user_group_id === userGroup && item.zone_id === zone)).length > 0;
        },
        toggle(zone, userGroup) {
            if (this.has(zone, userGroup)) {
                return this.trash(this.allocations.filter(item => (item.user_group_id === userGroup && item.zone_id === zone))[0].id);
            }
            // create!
            this.create(zone, userGroup);
        }
    },
}
</script>

<style scoped>
.ghost {
    opacity: 0.5;
    background: #c8ebfb;
}
</style>
